import { AlertTriangle, Inbox, RefreshCw, ShieldAlert, WifiOff } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Button } from './Button';

/**
 * Intentional loading / empty / error states.
 *
 * Every screen uses these rather than rendering a blank area or an empty
 * table with no explanation — "no deals currently qualify" is information,
 * an empty grid is not.
 */

export function Skeleton({
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('skeleton h-4 w-full', className)} {...rest} />;
}

/** Table placeholder that matches the real row height, so nothing jumps. */
export function TableSkeleton({ rows = 8, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-[var(--color-border)]">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-3.5 py-3">
          {Array.from({ length: columns }).map((_, c) => (
            <Skeleton
              key={c}
              className={cn('h-3.5', c === 0 ? 'w-[22%]' : c === columns - 1 ? 'w-[10%]' : 'w-[12%]')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function MetricsSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="divide-y divide-[var(--color-border)]">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center justify-between gap-6 py-2">
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="h-3.5 w-20" />
        </div>
      ))}
    </div>
  );
}

export function ChartSkeleton({ height = 220 }: { height?: number }) {
  return <Skeleton className="w-full" style={{ height }} />;
}

/**
 * Empty state: quiet, informative, and where relevant it states the
 * thresholds that were applied, so "nothing here" is explainable.
 */
export function EmptyState({
  title,
  description,
  criteria,
  action,
  icon: Icon = Inbox,
  className,
}: {
  title: string;
  description?: string;
  criteria?: { label: string; value: string }[];
  action?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      <Icon className="mb-3 h-4 w-4 text-[var(--color-text-subtle)]" />
      <p className="text-[13px] font-medium text-[var(--color-text)]">{title}</p>
      {description ? (
        <p className="mt-1 max-w-md text-[12.5px] leading-relaxed text-[var(--color-text-muted)]">
          {description}
        </p>
      ) : null}
      {criteria && criteria.length > 0 ? (
        <dl className="mt-3 w-full max-w-xs space-y-1 text-left">
          {criteria.map((c) => (
            <div key={c.label} className="flex justify-between gap-4 text-[12px]">
              <dt className="text-[var(--color-text-subtle)]">{c.label}</dt>
              <dd className="tnum text-[var(--color-text-muted)]">{c.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/** Error state that distinguishes network, permission and server failures. */
export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const apiError = error instanceof ApiError ? error : null;
  const offline = apiError?.status === 0;
  const forbidden = apiError?.isForbidden ?? false;

  const Icon = offline ? WifiOff : forbidden ? ShieldAlert : AlertTriangle;
  const title = offline
    ? 'Cannot reach the API'
    : forbidden
      ? 'You do not have access to this'
      : 'Something went wrong';
  const message =
    error instanceof Error ? error.message : 'An unexpected error occurred while loading this view.';

  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      <Icon
        className={cn(
          'mb-3 h-4 w-4',
          forbidden ? 'text-[var(--color-warning)]' : 'text-[var(--color-negative)]',
        )}
      />
      <p className="text-[13px] font-medium text-[var(--color-text)]">{title}</p>
      <p className="mt-1 max-w-md text-[12.5px] leading-relaxed text-[var(--color-text-muted)]">{message}</p>
      {onRetry && !forbidden ? (
        <Button variant="default" size="sm" className="mt-4" onClick={onRetry}>
          <RefreshCw className="h-3 w-3" />
          Retry
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Convenience wrapper that resolves the three states in one place, so pages
 * do not each re-implement the branching.
 */
export function QueryBoundary({
  isLoading,
  error,
  isEmpty,
  onRetry,
  loadingFallback,
  emptyFallback,
  children,
}: {
  isLoading: boolean;
  error: unknown;
  isEmpty?: boolean;
  onRetry?: () => void;
  loadingFallback?: React.ReactNode;
  emptyFallback?: React.ReactNode;
  children: React.ReactNode;
}) {
  if (isLoading) return <>{loadingFallback ?? <TableSkeleton />}</>;
  if (error) return <ErrorState error={error} {...(onRetry ? { onRetry } : {})} />;
  if (isEmpty) return <>{emptyFallback ?? <EmptyState title="No data" />}</>;
  return <>{children}</>;
}
